import type { ApiProblem, ApiSchemas, UploadLink } from '@rental/contracts/client'
import type { CaptureItem, DamageItem, PhotoItem } from './item'
import {
  backoffMs,
  damageIdOf,
  nextToSend,
  nextWakeAt,
  POST_TIMEOUT_MS,
  putTimeoutMs,
  settled,
  verdictOf,
} from './queue'
import type { CaptureStore } from './store'

export type Answer<T> = { ok: true; data: T } | { ok: false; problem: ApiProblem }

/** The three requests the queue makes; each gives up when its signal aborts. */
export interface CaptureTransport {
  postPhoto(
    item: PhotoItem,
    damageId: string | null,
    signal: AbortSignal,
  ): Promise<Answer<ApiSchemas['InspectionPhotoCreated']>>
  putPhoto(link: UploadLink, blob: Blob, signal: AbortSignal): Promise<ApiProblem | null>
  postDamage(item: DamageItem, signal: AbortSignal): Promise<Answer<{ id: string }>>
}

export interface DrainerDeps {
  store: CaptureStore
  transport: CaptureTransport
  /**
   * Who is unlocked on this device right now, or null: locked, turned to the
   * customer, enrolling, or the page out of sight. Asked again before every
   * request, so a switch between two requests stops the second.
   */
  sender: () => { userId: string } | null
  /** A 401 or a 426 goes to the screens' takeOver; true when it moved the device. */
  onAccess: (problem: ApiProblem) => boolean
  /** One sender across this browser's tabs (Web Locks), held for one item at a time. */
  exclusive: <T>(work: () => Promise<T>) => Promise<T>
  now: () => number
  setTimer: (run: () => void, ms: number) => unknown
  clearTimer: (timer: unknown) => void
  /** The items changed: the screens read them again, and so do the other tabs. */
  changed: () => void
  /** An item reached the server (as recorded, with the server's id): its inspection is read again. */
  sent: (item: CaptureItem) => void
  /** Something other than an answer failed (the store): reported, and the queue tries again. */
  onError: (error: unknown) => void
}

/** After a failure of the queue itself (not of a request): how long before it tries again. */
export const AFTER_ERROR_MS = 30_000

/**
 * What one item's send leaves for the pass: go on, stop (no connection, or
 * the person changed), nothing left, or go on after a request that ran out
 * of time (twice in a row, and the pass stops: the line is only slow).
 */
type Step = 'next' | 'stop' | 'idle' | 'slow'

/**
 * S4 (OPS-5): sends the queue, one item at a time, while its person is
 * unlocked. Started after a capture, when the connection comes back, when
 * the app comes back to the front, after an unlock, and when an item's wait
 * runs out. Never two passes at once in a tab, never two senders across tabs.
 */
export function createDrainer(deps: DrainerDeps) {
  let running: Promise<void> | null = null
  let again = false
  let stopped = false
  let wake: unknown = null
  /**
   * No connection for one item is none for the next: a dropped connection
   * holds the whole queue, longer after each one in a row (5 s, 15 s, ... as
   * an item's backoff), until any answer comes back or a sign that the
   * connection may be back (resume) comes first.
   */
  let holdUntil = 0
  let dropped = 0

  const mine = (userId: string) => deps.sender()?.userId === userId

  function kick(): void {
    if (stopped) return
    if (running !== null) {
      again = true
      return
    }
    running = pass()
      .catch((error: unknown) => {
        deps.onError(error)
        wakeIn(AFTER_ERROR_MS)
      })
      .finally(() => {
        running = null
        if (again) {
          again = false
          kick()
        }
      })
  }

  function wakeIn(ms: number): void {
    if (stopped) return
    if (wake !== null) deps.clearTimer(wake)
    wake = deps.setTimer(() => {
      wake = null
      kick()
    }, ms)
  }

  async function pass(): Promise<void> {
    if (wake !== null) deps.clearTimer(wake)
    wake = null
    try {
      let slow = 0
      for (;;) {
        if (deps.now() < holdUntil) return
        const who = deps.sender()
        if (!who) return
        const step = await deps.exclusive(() => sendNext(who.userId))
        slow = step === 'slow' ? slow + 1 : 0
        if (step === 'stop' || step === 'idle') return
        // Twice in a row without an answer in time: the line is too slow for now.
        if (slow >= 2) return hold()
      }
    } finally {
      await scheduleWake()
    }
  }

  function hold(): void {
    dropped++
    holdUntil = deps.now() + backoffMs(dropped)
  }

  /** Wakes up when this person's next item comes due. */
  async function scheduleWake(): Promise<void> {
    const who = deps.sender()
    if (!who || stopped) return
    const at = nextWakeAt(await deps.store.all(), who.userId)
    if (at !== null) wakeIn(Math.max(1_000, at - deps.now(), holdUntil - deps.now()))
  }

  /** Under the lock: picks this person's next item and marks it on its way. */
  async function sendNext(userId: string): Promise<Step> {
    // The lock may have taken a while: the person may have changed meanwhile.
    if (!mine(userId)) return 'stop'
    const now = deps.now()
    const chosen: { item: CaptureItem | null; changed: boolean } = {
      item: null,
      changed: false,
    }
    await deps.store.mutate((items) => {
      // Only the lock's holder sends: an item still "on its way" was left by a closed tab.
      const left = items
        .filter((item) => item.state === 'sending')
        .map((item): CaptureItem => ({ ...item, state: 'queued' }))
      const gone = new Set(settled(items, null, now))
      const leftIds = new Set(left.map((item) => item.id))
      const current = [
        ...items.filter((item) => !gone.has(item.id) && !leftIds.has(item.id)),
        ...left,
      ]
      const next = nextToSend(current, userId, now)
      if (next) {
        const byId = new Map(current.map((item) => [item.id, item]))
        chosen.item =
          next.kind === 'photo'
            ? { ...next, state: 'sending', damageId: damageIdOf(next, byId) }
            : { ...next, state: 'sending' }
      }
      const put = left.filter((item) => item.id !== chosen.item?.id)
      chosen.changed = left.length > 0 || gone.size > 0 || chosen.item !== null
      return {
        put: chosen.item ? [...put, chosen.item] : put,
        remove: [...gone],
      }
    })
    if (chosen.changed) deps.changed()
    const item = chosen.item
    if (!item) return 'idle'
    return item.kind === 'damage' ? sendDamage(item, userId) : sendPhoto(item, userId)
  }

  /** The request, given up after `ms`; `timedOut` when that is why it failed. */
  async function timed<T>(
    ms: number,
    request: (signal: AbortSignal) => Promise<T>,
  ): Promise<{ answer: T; timedOut: boolean }> {
    const controller = new AbortController()
    const timer = deps.setTimer(() => controller.abort(), ms)
    try {
      const answer = await request(controller.signal)
      return { answer, timedOut: controller.signal.aborted }
    } finally {
      deps.clearTimer(timer)
    }
  }

  async function sendDamage(item: DamageItem, userId: string): Promise<Step> {
    if (!mine(userId)) return putBack(item)
    const { answer, timedOut } = await timed(POST_TIMEOUT_MS, (signal) =>
      deps.transport.postDamage(item, signal),
    )
    if (!answer.ok) return failed(item, answer.problem, timedOut)
    const serverId = answer.data.id
    await deps.store.mutate((items) => {
      const current = items.find((candidate) => candidate.id === item.id)
      if (!current) return {}
      // Its photos waiting on this iPad now name the damage as the server knows it.
      const photos = items.flatMap((candidate) =>
        candidate.kind === 'photo' && candidate.damageItemId === item.id && !candidate.damageId
          ? [{ ...candidate, damageId: serverId }]
          : [],
      )
      return { put: [recorded(current, serverId), ...photos] }
    })
    deps.changed()
    deps.sent(recorded(item, serverId))
    return 'next'
  }

  async function sendPhoto(item: PhotoItem, userId: string): Promise<Step> {
    // A link ran out or a resend found the bytes there: one more question, never a loop.
    for (let asks = 0; asks < 2; asks++) {
      if (!mine(userId)) return putBack(item)
      const asked = await timed(POST_TIMEOUT_MS, (signal) =>
        deps.transport.postPhoto(item, item.damageId, signal),
      )
      if (!asked.answer.ok) return failed(item, asked.answer.problem, asked.timedOut)
      const { photo, upload } = asked.answer.data
      if (!upload) return done(item, photo.id)
      // Switched while the request was out: the bytes wait for their own person.
      if (!mine(userId)) return putBack(item)
      const put = await timed(putTimeoutMs(item.sizeBytes), (signal) =>
        deps.transport.putPhoto(upload, new Blob([item.bytes], { type: 'image/jpeg' }), signal),
      )
      if (!put.answer) return done(item, photo.id)
      const verdict = verdictOf(put.answer)
      if (verdict.kind === 'renew' || verdict.kind === 'stored') {
        if (asks === 0) continue
      }
      return failed(item, put.answer, put.timedOut)
    }
    return 'next'
  }

  function recorded(item: CaptureItem, serverId: string): CaptureItem {
    dropped = 0
    return {
      ...item,
      state: 'sent',
      serverId,
      sentAt: deps.now(),
      attempts: 0,
      problem: null,
    }
  }

  async function done(item: PhotoItem, serverId: string): Promise<Step> {
    await change(item.id, (current) => recorded(current, serverId))
    deps.sent(recorded(item, serverId))
    return 'next'
  }

  /** Not sent, through no fault of its own: waits for its person, as it was. */
  async function putBack(item: CaptureItem): Promise<Step> {
    await change(item.id, (current) => ({ ...current, state: 'queued' }))
    return 'stop'
  }

  async function failed(item: CaptureItem, problem: ApiProblem, timedOut = false): Promise<Step> {
    const verdict = verdictOf(problem)
    // An answer: the connection is there. Asked to slow down (429) is not one to count.
    if (problem.kind === 'api' && problem.status !== 429) dropped = 0
    if (verdict.kind === 'later') {
      // No connection at all: the queue waits as a whole (hold), not this item.
      const offline = verdict.stopPass && !timedOut
      await change(item.id, (current) => {
        const attempts = current.attempts + 1
        return {
          ...current,
          state: 'queued',
          attempts,
          nextAttemptAt: offline ? current.nextAttemptAt : deps.now() + backoffMs(attempts),
          problem,
        }
      })
      // No answer in time is this request's trouble, maybe this item's: the next one goes.
      if (timedOut) return 'slow'
      if (!offline) return verdict.stopPass ? 'stop' : 'next'
      hold()
      return 'stop'
    }
    if (verdict.kind === 'access' && deps.onAccess(problem)) return putBack(item)
    // Kept, with why, until its person tries again or discards it: never dropped.
    await change(item.id, (current) => ({
      ...current,
      state: 'refused',
      problem,
    }))
    return 'next'
  }

  async function change(id: string, update: (item: CaptureItem) => CaptureItem): Promise<void> {
    await deps.store.mutate((items) => {
      const current = items.find((item) => item.id === id)
      return current ? { put: [update(current)] } : {}
    })
    deps.changed()
  }

  return {
    /** A pass, unless the queue is waiting out a dropped connection. */
    kick,
    /** The connection may be back (online, the app in front, an unlock, a capture): a pass now. */
    resume(): void {
      holdUntil = 0
      kick()
    },
    /** Resolves when the pass under way, if any, has ended (for tests and for the lock screen). */
    async idle(): Promise<void> {
      while (running !== null) await running
    },
    stop(): void {
      stopped = true
      if (wake !== null) deps.clearTimer(wake)
      wake = null
    },
  }
}

export type Drainer = ReturnType<typeof createDrainer>
